import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Calendar, 
  Clock, 
  Users, 
  MapPin, 
  MessageSquare, 
  Paperclip,
  Send,
  Edit,
  Trash2,
  Eye
} from 'lucide-react';
import CreateMeetingModal from './CreateMeetingModal';
import MeetingDetailsModal from './MeetingDetailsModal';
import { MeetingService, Meeting, MeetingCreateData } from '../../services/meeting';

// Meeting interface is now imported from the service

const AgendaTab: React.FC = () => {
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [meetingToDelete, setMeetingToDelete] = useState<Meeting | null>(null);

  // Load meetings from API
  useEffect(() => {
    loadMeetings();
  }, []);

  const loadMeetings = async () => {
    try {
      setIsLoading(true);
      const meetingsData = await MeetingService.getMeetings();
      // Ensure meetingsData is an array
      setMeetings(Array.isArray(meetingsData) ? meetingsData : []);
    } catch (error) {
      console.error('Failed to load meetings:', error);
      // Set empty array on error
      setMeetings([]);
      // You could show an error toast here
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateMeeting = async (meetingData: MeetingCreateData) => {
    try {
      // Separate attachments from meeting data
      const { attachments, ...meetingDataWithoutAttachments } = meetingData;
      
      // Create meeting first (without attachments)
      const newMeeting = await MeetingService.createMeeting(meetingDataWithoutAttachments);
      console.log('Created meeting:', newMeeting);
      
      // Upload attachments if any
      if (attachments && attachments.length > 0) {
        console.log('Uploading', attachments.length, 'attachments...');
        for (const file of attachments) {
          try {
            await MeetingService.uploadAttachment(newMeeting.id, file, file.name);
            console.log('Uploaded attachment:', file.name);
          } catch (error) {
            console.error('Failed to upload attachment:', file.name, error);
          }
        }
      }
      
      setMeetings([...meetings, newMeeting]);
      setIsCreateModalOpen(false);
      
      // Send invitations after everything is set up
      if (newMeeting && newMeeting.id) {
        try {
          await MeetingService.sendInvitations(newMeeting.id);
        } catch (error) {
          console.error('Failed to send invitations:', error);
        }
      } else {
        console.warn('Meeting created but no ID returned:', newMeeting);
      }
    } catch (error) {
      console.error('Failed to create meeting:', error);
    }
  };

  const handleUpdateMeeting = async (meetingData: MeetingCreateData) => {
    if (!selectedMeeting) return;
    
    try {
      // Separate attachments from meeting data
      const { attachments, ...meetingDataWithoutAttachments } = meetingData;
      
      // Update meeting first (without attachments)
      const updatedMeeting = await MeetingService.updateMeeting(selectedMeeting.id, meetingDataWithoutAttachments);
      console.log('Updated meeting:', updatedMeeting);
      
      // Upload new attachments if any
      if (attachments && attachments.length > 0) {
        console.log('Uploading', attachments.length, 'new attachments...');
        for (const file of attachments) {
          // Only upload if it's a File object (new attachment)
          if (file instanceof File) {
            try {
              await MeetingService.uploadAttachment(updatedMeeting.id, file, file.name);
              console.log('Uploaded new attachment:', file.name);
            } catch (error) {
              console.error('Failed to upload attachment:', file.name, error);
            }
          }
        }
      }
      
      // Update the meeting in the list
      setMeetings(meetings.map(m => m.id === selectedMeeting.id ? updatedMeeting : m));
      setIsCreateModalOpen(false);
      
      // Send updated invitations to notify attendees of changes
      if (updatedMeeting && updatedMeeting.id) {
        try {
          console.log('Sending invitations for updated meeting ID:', updatedMeeting.id);
          const invitationResult = await MeetingService.sendInvitations(updatedMeeting.id);
          console.log('Updated meeting invitations sent successfully:', invitationResult);
        } catch (error) {
          console.error('Failed to send updated invitations:', error);
        }
      } else {
        console.warn('No meeting ID found for sending invitations:', updatedMeeting);
      }
      
    } catch (error) {
      console.error('Failed to update meeting:', error);
    }
  };

  const handleViewMeeting = (meeting: Meeting) => {
    setSelectedMeeting(meeting);
    setIsDetailsModalOpen(true);
  };

  const handleDeleteMeeting = (meeting: Meeting) => {
    setMeetingToDelete(meeting);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteMeeting = async () => {
    if (!meetingToDelete) return;
    
    try {
      await MeetingService.deleteMeeting(meetingToDelete.id);
      setMeetings(meetings.filter(m => m.id !== meetingToDelete.id));
      setShowDeleteConfirm(false);
      setMeetingToDelete(null);
    } catch (error) {
      console.error('Failed to delete meeting:', error);
      // You could show an error toast here
    }
  };

  const cancelDeleteMeeting = () => {
    setShowDeleteConfirm(false);
    setMeetingToDelete(null);
  };

  // Use utility functions from MeetingService
  const formatDate = MeetingService.formatDate;
  const formatTime = MeetingService.formatTime;
  const groupMeetingsByDate = MeetingService.groupMeetingsByDate;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const groupedMeetings = groupMeetingsByDate(meetings || []);
  const sortedDates = Object.keys(groupedMeetings).sort().reverse();

  return (
    <div className="space-y-6">
      {/* Header with Create Button */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Meeting Agenda</h2>
          <p className="text-gray-600">Schedule and manage team meetings</p>
        </div>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus size={20} />
          Schedule Meeting
        </button>
      </div>

      {/* Meetings List */}
      <div className="space-y-6">
        {sortedDates.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No meetings scheduled</h3>
            <p className="mt-2 text-gray-500">Get started by scheduling your first meeting.</p>
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus size={18} />
              Schedule Meeting
            </button>
          </div>
        ) : (
          sortedDates.map(date => (
            <div key={date} className="bg-white rounded-lg shadow border">
              {/* Date Header */}
              <div className="bg-gray-50 px-6 py-3 border-b">
                <h3 className="text-lg font-medium text-gray-900">
                  {formatDate(date)}
                </h3>
              </div>
              
              {/* Meetings for this date */}
              <div className="divide-y divide-gray-200">
                {groupedMeetings[date].map(meeting => (
                  <div key={meeting.id} className="p-6 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-4 mb-2">
                          <h4 className="text-lg font-medium text-gray-900">
                            {meeting.title}
                          </h4>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {meeting.workshop_count} workshop{meeting.workshop_count !== 1 ? 's' : ''}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <Clock size={16} />
                            <span>{formatTime(meeting.time, meeting.duration)}</span>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <MapPin size={16} />
                            <span>{meeting.location}</span>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Users size={16} />
                            <span>{meeting.attendee_count} attendee{meeting.attendee_count !== 1 ? 's' : ''}</span>
                          </div>
                          
                          {meeting.message && (
                            <div className="flex items-center gap-2">
                              <MessageSquare size={16} />
                              <span className="truncate">Message included</span>
                            </div>
                          )}
                        </div>
                        
                        {meeting.message && (
                          <p className="mt-3 text-sm text-gray-700 line-clamp-2">
                            {meeting.message}
                          </p>
                        )}
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-2 ml-4">
                        <button
                          onClick={() => handleViewMeeting(meeting)}
                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="View details"
                        >
                          <Eye size={18} />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedMeeting(meeting);
                            setIsCreateModalOpen(true);
                          }}
                          className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title="Edit meeting"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => handleDeleteMeeting(meeting)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete meeting"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modals */}
      {isCreateModalOpen && (
        <CreateMeetingModal
          isOpen={isCreateModalOpen}
          onClose={() => {
            setIsCreateModalOpen(false);
            setSelectedMeeting(null);
          }}
          onSubmit={selectedMeeting ? handleUpdateMeeting : handleCreateMeeting}
          editingMeeting={selectedMeeting}
        />
      )}

      {isDetailsModalOpen && selectedMeeting && (
        <MeetingDetailsModal
          isOpen={isDetailsModalOpen}
          onClose={() => {
            setIsDetailsModalOpen(false);
            setSelectedMeeting(null);
          }}
          meeting={selectedMeeting}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && meetingToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Delete Meeting
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "<strong>{meetingToDelete.title}</strong>"? 
              This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelDeleteMeeting}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteMeeting}
                className="px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-700 rounded-md"
              >
                Delete Meeting
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgendaTab;
